// =====================================================
// Floor Plan Card - Display Floor Plan Details with Photo Carousel
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { FloorPlanCard as FloorPlanCardType } from '../../types';
import TourRequestForm from '../TourRequestForm';
import { useChatStore } from '../../store/chatStore';

interface FloorPlanCardProps {
  card: FloorPlanCardType;
  onFormToggle?: (isOpen: boolean) => void;
}

const AUTOPLAY_INTERVAL = 4000; // 4 seconds between slides

const FloorPlanCard: React.FC<FloorPlanCardProps> = ({ card, onFormToggle }) => {
  const { isFloorPlanSubmitted, openPhotoModal } = useChatStore();
  const [showTourForm, setShowTourForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [validImages, setValidImages] = useState<string[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const isSubmitted = isFloorPlanSubmitted(card.plan_id);

  // Notify parent when form opens/closes
  const handleFormToggle = (isOpen: boolean) => {
    setShowTourForm(isOpen);
    onFormToggle?.(isOpen);
  };

  // Filter out broken/invalid images on mount
  useEffect(() => {
    const checkImages = async () => {
      const imagesToCheck = card.images || (card.image_url ? [card.image_url] : []);
      const validUrls: string[] = [];

      for (const url of imagesToCheck) {
        if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
          continue;
        }

        const trimmedUrl = url.trim();
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://') && !trimmedUrl.startsWith('/')) {
          continue;
        }

        try {
          const isValid = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = trimmedUrl;
            setTimeout(() => resolve(false), 5000);
          });

          if (isValid) {
            validUrls.push(trimmedUrl);
          }
        } catch {
          // Skip broken images
        }
      }

      if (validUrls.length === 0) {
        validUrls.push('https://via.placeholder.com/400x300?text=Floor+Plan');
      }

      setValidImages(validUrls);
      setImagesLoaded(true);
    };

    checkImages();
  }, [card.images, card.image_url]);

  // Autoplay functionality
  useEffect(() => {
    if (!imagesLoaded || validImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev < validImages.length - 1 ? prev + 1 : 0
      );
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [imagesLoaded, validImages.length]);

  const formatPrice = (price?: number) => {
    if (!price) return 'Contact for pricing';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Format description: split into intro paragraphs and bullet points
  const formatDescription = (description: string) => {
    // Check if description has a "features:" pattern
    const featuresMatch = description.match(/^([\s\S]*?features:)\s*/i);
    if (!featuresMatch) {
      return { introParagraphs: [description], features: [] };
    }

    const introText = featuresMatch[1];
    const featuresText = description.substring(featuresMatch[0].length);

    // Split intro into paragraphs (e.g., "...buyers!The Victoria..." -> two paragraphs)
    const introParagraphs = introText
      .replace(/([.!?])([A-Z])/g, '$1|||$2')
      .split('|||')
      .map(p => p.trim())
      .filter(p => p.length > 0);

    // Split features by inserting delimiter before capital letters that follow lowercase
    // Using replace + split for better browser compatibility (no lookbehind)
    const features = featuresText
      .replace(/([a-z])([A-Z])/g, '$1|||$2')
      .split('|||')
      .map(f => f.trim())
      .filter(f => f.length > 0);

    return { introParagraphs, features };
  };

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : validImages.length - 1
    );
  }, [validImages.length]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev < validImages.length - 1 ? prev + 1 : 0
    );
  }, [validImages.length]);

  // Handle image click to open photo enlargement modal
  const handleImageClick = () => {
    if (validImages.length > 0) {
      openPhotoModal(validImages, currentImageIndex, (finalIndex) => {
        // Sync carousel to the image user was viewing when modal closed
        setCurrentImageIndex(finalIndex);
      }, card.plan_name);
    }
  };

  // Show tour request form instead of card when requested
  if (showTourForm) {
    return (
      <TourRequestForm
        isOpen={showTourForm}
        onClose={() => handleFormToggle(false)}
        prefillData={{
          cityOfInterest: card.region_name || '',
          communityOfInterest: card.community_name || '',
          addressOrFloorPlan: card.plan_name,
          // Separate column for floor plan name
          floorPlanName: card.plan_name,
          // Track as Floor Plan inquiry
          inquiryType: 'floor_plan',
          floorPlanId: card.plan_id,
          communityId: card.community_id,
          regionId: card.region_id
        }}
      />
    );
  }

  return (
    <div className="chat-card animate-slide-up">
      {/* Image Carousel */}
      <div className="relative h-48 bg-gray-200">
        {!imagesLoaded ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-pulse bg-gray-300 w-full h-full" />
          </div>
        ) : (
          <>
            <img
              src={validImages[currentImageIndex]}
              alt={`${card.plan_name} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-opacity duration-300 cursor-pointer"
              onClick={handleImageClick}
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Floor+Plan';
              }}
            />

            {/* Navigation Arrows - only show if multiple images */}
            {validImages.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white
                             rounded-full p-1.5 shadow-lg transition-all z-10"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white
                             rounded-full p-1.5 shadow-lg transition-all z-10"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Dot Indicators */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {validImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex
                          ? 'bg-white scale-110'
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Available Homes Badge */}
            {card.available_homes !== undefined && card.available_homes > 0 && (
              <div className="absolute top-2 right-2 bg-green-600 text-white
                              px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                {card.available_homes} Available
              </div>
            )}
          </>
        )}
      </div>

      {/* Expand Hint */}
      <p className="text-[10px] text-gray-400 text-center py-1">Click image to expand</p>

      {/* Details */}
      <div className="p-4">
        <h3 className="font-bold text-lg text-gray-900 mb-1">
          {card.plan_name}
        </h3>

        {card.base_price && (
          <p className="text-sm text-betenbough-olive font-semibold mb-3">
            Starting from {formatPrice(card.base_price)}
          </p>
        )}

        {/* Specs - single row, compact */}
        <div className="flex items-center space-x-3 text-xs text-gray-700">
          <div className="flex items-center space-x-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>{card.bedrooms} bed</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            <span>{card.bathrooms} bath</span>
          </div>
          {card.garage_spaces !== undefined && card.garage_spaces > 0 && (
            <div className="flex items-center space-x-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span>{card.garage_spaces}-car</span>
            </div>
          )}
          {card.sqft && (
            <div className="flex items-center space-x-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <span>{card.sqft.toLocaleString()} sqft</span>
            </div>
          )}
        </div>

        {/* Short Description - Expandable with formatted bullets */}
        {card.short_description && (() => {
          const { introParagraphs, features } = formatDescription(card.short_description);
          const hasFeatures = features.length > 0;
          const truncateLength = 80;
          const shouldTruncate = card.short_description.length > truncateLength;

          return (
            <div className="mt-3">
              {!isDescriptionExpanded ? (
                // Collapsed: show 2 lines with "Read more" at end
                <div className="relative">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {card.short_description}
                  </p>
                  {shouldTruncate && (
                    <button
                      onClick={() => setIsDescriptionExpanded(true)}
                      className="absolute bottom-0 right-0 text-sm text-betenbough-olive hover:underline bg-white pl-1"
                    >
                      ... Read more
                    </button>
                  )}
                </div>
              ) : (
                // Expanded: show formatted with paragraphs and bullets
                <>
                  <div className="text-sm text-gray-600 space-y-1">
                    {introParagraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                    {hasFeatures && (
                      <ul className="list-disc list-inside space-y-0.5">
                        {features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    onClick={() => setIsDescriptionExpanded(false)}
                    className="text-xs text-betenbough-olive hover:underline mt-1"
                  >
                    Show less
                  </button>
                </>
              )}
            </div>
          );
        })()}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          {card.details_url && (
            <a
              href={card.details_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1 bg-white hover:bg-gray-50 text-betenbough-olive border
                         border-betenbough-olive px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Floor Plan
            </a>
          )}
          {isSubmitted ? (
            <button
              disabled
              className="flex-1 inline-flex items-center justify-center gap-1 bg-green-600 text-white
                         px-2 py-1.5 rounded-lg text-xs font-medium cursor-default whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Request Submitted
            </button>
          ) : (
            <button
              onClick={() => handleFormToggle(true)}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-betenbough-olive hover:bg-betenbough-olive/90 text-white
                         px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Request Tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloorPlanCard;
